// client/src/components/admin/ApiKeysSettings.js
// Admin page for managing external API keys — stored in Settings DB, process.env fallback.
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, CircularProgress,
  Snackbar, Alert, Paper, Divider
} from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useNavigate } from 'react-router-dom';
import AppBarHeader from '../AppBarHeader';
import axiosInstance from '../../axiosInstance';
import { jwtDecode } from 'jwt-decode';

// All DB keys managed on this page, with their display labels and whether they are secrets
const ALL_KEYS = [
  // RAWG
  { db: 'rawgApiKey',            label: 'API Key',              secret: true },
  // IGDB
  { db: 'igdbClientId',          label: 'Client ID',            secret: false },
  { db: 'igdbClientSecret',      label: 'Client Secret',        secret: true },
  // OMDB
  { db: 'omdbApiKey',            label: 'API Key',              secret: true },
  // Rebrickable
  { db: 'rebrickableApiKey',     label: 'API Key',              secret: true },
  // Exchange Rate API
  { db: 'exchangeApiKey',        label: 'API Key',              secret: true },
  // ScanDex
  { db: 'scandexBaseUrl',        label: 'Base URL',             secret: false },
  { db: 'scandexApiToken',       label: 'API Token',            secret: true },
  // eBay
  { db: 'ebayAppId',             label: 'App ID (Client ID)',   secret: false },
  { db: 'ebayCertId',            label: 'Cert ID (Client Secret)', secret: true },
  { db: 'ebayMarketplaceId',     label: 'Marketplace ID',       secret: false },
  { db: 'ebayVerificationToken', label: 'Verification Token',   secret: true },
  { db: 'ebayDeletionEndpointUrl', label: 'Deletion Endpoint URL', secret: false },
  // Brickset
  { db: 'bricksetApiKey',        label: 'API Key',              secret: true },
  // Discogs
  { db: 'discogsToken',          label: 'Personal Access Token', secret: true },
  // Pokémon TCG
  { db: 'pokemonTcgApiKey',      label: 'API Key',              secret: false },
  // ComicVine
  { db: 'comicVineApiKey',       label: 'API Key',              secret: true },
];

const SECTIONS = [
  {
    title: 'RAWG',
    description: 'Video game search and metadata (rawg.io)',
    keys: ['rawgApiKey'],
  },
  {
    title: 'IGDB',
    description: 'Game and console search via Twitch/IGDB',
    keys: ['igdbClientId', 'igdbClientSecret'],
  },
  {
    title: 'OMDB',
    description: 'Movie search and metadata (omdbapi.com)',
    keys: ['omdbApiKey'],
  },
  {
    title: 'Rebrickable',
    description: 'LEGO set search and data',
    keys: ['rebrickableApiKey'],
  },
  {
    title: 'Exchange Rate API',
    description: 'Currency conversion (exchangeratesapi.io)',
    keys: ['exchangeApiKey'],
  },
  {
    title: 'ScanDex',
    description: 'Barcode lookup for games and consoles',
    keys: ['scandexBaseUrl', 'scandexApiToken'],
  },
  {
    title: 'eBay',
    description: 'Market price lookups and account deletion compliance',
    keys: ['ebayAppId', 'ebayCertId', 'ebayMarketplaceId', 'ebayVerificationToken', 'ebayDeletionEndpointUrl'],
  },
  {
    title: 'Brickset',
    description: 'LEGO barcode (EAN) enrichment — register free at brickset.com/api',
    keys: ['bricksetApiKey'],
  },
  {
    title: 'Discogs',
    description: 'Vinyl record search — get a free Personal Access Token at discogs.com/settings/developers',
    keys: ['discogsToken'],
  },
  {
    title: 'Pokémon TCG',
    description: 'Optional. Raises rate limit from 1,000 to 20,000 requests/day. Get a free key at pokemontcg.io.',
    keys: ['pokemonTcgApiKey'],
  },
  {
    title: 'ComicVine',
    description: 'Required for comic issue search. Free API key at comicvine.gamespot.com.',
    keys: ['comicVineApiKey'],
  },
];

const KEY_META = Object.fromEntries(ALL_KEYS.map(k => [k.db, k]));

function SecretField({ dbKey, label, value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <Box sx={{ mb: 2 }}>
      {show ? (
        <TextField
          label={label}
          fullWidth
          value={value}
          onChange={e => onChange(dbKey, e.target.value)}
          InputProps={{
            endAdornment: (
              <Button size="small" onClick={() => setShow(false)}>Hide</Button>
            ),
          }}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            label={label}
            fullWidth
            value={value ? '••••••••' : ''}
            disabled
            placeholder="(not set)"
          />
          <Button variant="outlined" size="small" onClick={() => setShow(true)}>
            {value ? 'Change' : 'Set'}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function ApiKeysSettings() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;
  const userId = user ? user.id : null;
  const isAdmin = user ? user.isAdmin : false;

  const [values, setValues] = useState(
    Object.fromEntries(ALL_KEYS.map(k => [k.db, '']))
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!isAdmin) { navigate('/dashboard'); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, navigate]);

  async function loadAll() {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/settings/api-keys-effective', authHeader);
      const data = res.data || {};
      setValues(prev => {
        const next = { ...prev };
        ALL_KEYS.forEach(k => { next[k.db] = data[k.db] ?? ''; });
        return next;
      });
    } catch {
      // Leave values as empty — admin can enter them manually
    } finally {
      setLoading(false);
    }
  }

  function handleChange(dbKey, val) {
    setValues(v => ({ ...v, [dbKey]: val }));
  }

  async function handleSaveSection(section) {
    setSaving(s => ({ ...s, [section.title]: true }));
    try {
      await Promise.all(
        section.keys.map(k =>
          axiosInstance.put(`/settings/${k}`, { value: values[k] }, authHeader)
        )
      );
      setSnackbar({ open: true, message: `${section.title} settings saved.`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: `Failed to save ${section.title} settings.`, severity: 'error' });
    } finally {
      setSaving(s => ({ ...s, [section.title]: false }));
    }
  }

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    navigate('/login');
  }, [navigate]);

  if (loading) {
    return (
      <>
        <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
      </>
    );
  }

  return (
    <>
      <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
      <Box sx={{ maxWidth: 720, margin: '0 auto', p: 4 }}>
        <Button variant="outlined" onClick={() => navigate('/admin')} sx={{ mb: 2 }}>
          ← Back to Admin Dashboard
        </Button>

        <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
          API Keys
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Keys are stored in the Settings database and read at call time — no server restart needed.
          Blank fields fall back to <code>.env</code> values if set.
        </Typography>

        {SECTIONS.map((section, idx) => {
          const isSaving = saving[section.title] || false;
          return (
            <Paper key={section.title} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>{section.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {section.description}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {section.keys.map(dbKey => {
                const meta = KEY_META[dbKey];
                if (meta.secret) {
                  return (
                    <SecretField
                      key={dbKey}
                      dbKey={dbKey}
                      label={meta.label}
                      value={values[dbKey]}
                      onChange={handleChange}
                    />
                  );
                }
                return (
                  <TextField
                    key={dbKey}
                    label={meta.label}
                    fullWidth
                    value={values[dbKey]}
                    onChange={e => handleChange(dbKey, e.target.value)}
                    sx={{ mb: 2 }}
                  />
                );
              })}

              <Button
                variant="contained"
                onClick={() => handleSaveSection(section)}
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress size={18} /> : <VpnKeyIcon />}
                sx={{ mt: 1 }}
              >
                {isSaving ? 'Saving…' : `Save ${section.title} Settings`}
              </Button>
            </Paper>
          );
        })}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ApiKeysSettings;
